/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import { useThemeObserver } from '@atlaskit/tokens';

// ── Sub-type tile icon imports ──────────────────────────────────────────────
// Dark mode
import areaChartDarkSvg from './assets/darkModeSubtypeIcons/area-chart.svg';
import barChartDarkSvg from './assets/darkModeSubtypeIcons/bar-chart.svg';
import boldDarkSvg from './assets/darkModeSubtypeIcons/bold.svg';
import flipCardsDarkSvg from './assets/darkModeSubtypeIcons/flip-cards.svg';
import flowchartDarkSvg from './assets/darkModeSubtypeIcons/flowchart.svg';
import funnelChartDarkSvg from './assets/darkModeSubtypeIcons/funnel-chart.svg';
import ganttChartDarkSvg from './assets/darkModeSubtypeIcons/gantt-chart.svg';
import geographicalMapDarkSvg from './assets/darkModeSubtypeIcons/geographical-map.svg';
import lineChartDarkSvg from './assets/darkModeSubtypeIcons/line-chart.svg';
import mindmapDarkSvg from './assets/darkModeSubtypeIcons/mindmap.svg';
import minimalistDarkSvg from './assets/darkModeSubtypeIcons/minimalist.svg';
import modernDarkSvg from './assets/darkModeSubtypeIcons/modern.svg';
import orgChartDarkSvg from './assets/darkModeSubtypeIcons/org-chart.svg';
import pieChartDarkSvg from './assets/darkModeSubtypeIcons/pie-chart.svg';
import quadrantDarkSvg from './assets/darkModeSubtypeIcons/quadrant.svg';
import sketchV2DarkSvg from './assets/darkModeSubtypeIcons/sketch-v2.svg';
import sketchDarkSvg from './assets/darkModeSubtypeIcons/sketch.svg';
import timelineDarkSvg from './assets/darkModeSubtypeIcons/timeline.svg';
import treemapDarkSvg from './assets/darkModeSubtypeIcons/treemap.svg';
// Light mode
import areaChartSvg from './assets/lightModeSubtypeIcons/area-chart.svg';
import barChartSvg from './assets/lightModeSubtypeIcons/bar-chart.svg';
import boldSvg from './assets/lightModeSubtypeIcons/bold.svg';
import flipCardsSvg from './assets/lightModeSubtypeIcons/flip-cards.svg';
import flowchartSvg from './assets/lightModeSubtypeIcons/flowchart.svg';
import funnelChartSvg from './assets/lightModeSubtypeIcons/funnel-chart.svg';
import ganttChartSvg from './assets/lightModeSubtypeIcons/gantt-chart.svg';
import geographicalMapSvg from './assets/lightModeSubtypeIcons/geographical-map.svg';
import imageSvg from './assets/lightModeSubtypeIcons/image.svg';
import lineChartSvg from './assets/lightModeSubtypeIcons/line-chart.svg';
import mindmapSvg from './assets/lightModeSubtypeIcons/mindmap.svg';
import minimalistSvg from './assets/lightModeSubtypeIcons/minimalist.svg';
import modernSvg from './assets/lightModeSubtypeIcons/modern.svg';
import orgChartSvg from './assets/lightModeSubtypeIcons/org-chart.svg';
import pieChartSvg from './assets/lightModeSubtypeIcons/pie-chart.svg';
import quadrantSvg from './assets/lightModeSubtypeIcons/quadrant.svg';
import sketchV2Svg from './assets/lightModeSubtypeIcons/sketch-v2.svg';
import sketchSvg from './assets/lightModeSubtypeIcons/sketch.svg';
import timelineSvg from './assets/lightModeSubtypeIcons/timeline.svg';
import treemapSvg from './assets/lightModeSubtypeIcons/treemap.svg';
import type { KnownSubType } from './types';

// ── Icon components ─────────────────────────────────────────────────────────

const iconStyles = css({
	width: '100%',
	height: '100%',
	objectFit: 'cover',
	display: 'block',
});

// Negative margins to counteract the default padding from the Pressable tile component,
// allowing the image icon to extend edge-to-edge within the tile.
const edgeToEdgeIconStyles = css({
	margin: '-1px -6px',
});

const SubTypeIcon = ({ src, srcDark, alt }: { src: string; srcDark: string; alt: string }) => {
	const { colorMode } = useThemeObserver();
	const resolvedSrc = colorMode === 'dark' ? srcDark : src;
	return <img src={resolvedSrc} alt={alt} css={iconStyles} aria-hidden="true" />;
};

type SubTypeIconComponentProps = { alt: string };
type SubTypeIconComponent = (props: SubTypeIconComponentProps) => JSX.Element;

// Charts
export const LineChartIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={lineChartSvg} srcDark={lineChartDarkSvg} alt={alt} />
);
export const AreaChartIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={areaChartSvg} srcDark={areaChartDarkSvg} alt={alt} />
);
export const BarChartIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={barChartSvg} srcDark={barChartDarkSvg} alt={alt} />
);
export const FunnelChartIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={funnelChartSvg} srcDark={funnelChartDarkSvg} alt={alt} />
);
export const PieChartIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={pieChartSvg} srcDark={pieChartDarkSvg} alt={alt} />
);
export const GanttChartIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={ganttChartSvg} srcDark={ganttChartDarkSvg} alt={alt} />
);

// Diagrams
export const MindMapIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={mindmapSvg} srcDark={mindmapDarkSvg} alt={alt} />
);
export const FlowChartIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={flowchartSvg} srcDark={flowchartDarkSvg} alt={alt} />
);

// Infographics
export const ModernStyleIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={modernSvg} srcDark={modernDarkSvg} alt={alt} />
);
export const BoldStyleIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={boldSvg} srcDark={boldDarkSvg} alt={alt} />
);
export const SketchStyleIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={sketchSvg} srcDark={sketchDarkSvg} alt={alt} />
);
export const SketchStyleV2Icon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={sketchV2Svg} srcDark={sketchV2DarkSvg} alt={alt} />
);
export const MinimalistStyleIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={minimalistSvg} srcDark={minimalistDarkSvg} alt={alt} />
);

// Visual aids
export const FlipCardIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={flipCardsSvg} srcDark={flipCardsDarkSvg} alt={alt} />
);
export const QuadrantIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={quadrantSvg} srcDark={quadrantDarkSvg} alt={alt} />
);
export const TreeMapIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={treemapSvg} srcDark={treemapDarkSvg} alt={alt} />
);
export const TimelineIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={timelineSvg} srcDark={timelineDarkSvg} alt={alt} />
);
export const GeographicalMapIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={geographicalMapSvg} srcDark={geographicalMapDarkSvg} alt={alt} />
);
export const OrgChartIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<SubTypeIcon src={orgChartSvg} srcDark={orgChartDarkSvg} alt={alt} />
);
const ImageIcon = ({ alt }: SubTypeIconComponentProps): JSX.Element => (
	<div css={edgeToEdgeIconStyles}>
		<SubTypeIcon src={imageSvg} srcDark={imageSvg} alt={alt} />
	</div>
);

/**
 * Maps each sub-type value to its light and dark SVG URLs.
 * Used to preload sub-type icons on hover before the accordion expands.
 */
export const SUB_TYPE_ICON_URLS: Record<KnownSubType, { light: string; dark: string }> = {
	// Charts
	'line-chart': { light: lineChartSvg, dark: lineChartDarkSvg },
	'area-chart': { light: areaChartSvg, dark: areaChartDarkSvg },
	'bar-chart': { light: barChartSvg, dark: barChartDarkSvg },
	'funnel-chart': { light: funnelChartSvg, dark: funnelChartDarkSvg },
	'pie-chart': { light: pieChartSvg, dark: pieChartDarkSvg },
	'gantt-chart': { light: ganttChartSvg, dark: ganttChartDarkSvg },
	// Diagrams
	mindmap: { light: mindmapSvg, dark: mindmapDarkSvg },
	flowchart: { light: flowchartSvg, dark: flowchartDarkSvg },
	// Infographics
	'corporate-modern': { light: modernSvg, dark: modernDarkSvg },
	'corporate-doodle': { light: sketchV2Svg, dark: sketchV2DarkSvg },
	charlie: { light: boldSvg, dark: boldDarkSvg },
	minimalist: { light: minimalistSvg, dark: minimalistDarkSvg },
	// Visual aids
	'flip-card': { light: flipCardsSvg, dark: flipCardsDarkSvg },
	quadrant: { light: quadrantSvg, dark: quadrantDarkSvg },
	'tree-map': { light: treemapSvg, dark: treemapDarkSvg },
	'horizontal-timeline': { light: timelineSvg, dark: timelineDarkSvg },
	map: { light: geographicalMapSvg, dark: geographicalMapDarkSvg },
	'org-chart': { light: orgChartSvg, dark: orgChartDarkSvg },
	image: { light: imageSvg, dark: imageSvg },
};

/**
 * Maps each sub-type value to its icon component.
 * Used by the sub-type grid to render the correct icon for each tile.
 *
 * Typed as `Record<KnownSubType, …>` so a missing entry is a compile-time error.
 */
export const SUB_TYPE_ICON_MAP: Record<KnownSubType, SubTypeIconComponent> = {
	// Charts
	'line-chart': LineChartIcon,
	'area-chart': AreaChartIcon,
	'bar-chart': BarChartIcon,
	'funnel-chart': FunnelChartIcon,
	'pie-chart': PieChartIcon,
	'gantt-chart': GanttChartIcon,
	// Diagrams
	mindmap: MindMapIcon,
	flowchart: FlowChartIcon,
	// Infographics
	'corporate-modern': ModernStyleIcon,
	'corporate-doodle': (props) =>
		fg('cc-maui-phase-2') ? <SketchStyleV2Icon {...props} /> : <SketchStyleIcon {...props} />,
	charlie: BoldStyleIcon,
	minimalist: MinimalistStyleIcon,
	// Visual aids
	'flip-card': FlipCardIcon,
	quadrant: QuadrantIcon,
	'tree-map': TreeMapIcon,
	'horizontal-timeline': TimelineIcon,
	map: GeographicalMapIcon,
	'org-chart': OrgChartIcon,
	image: ImageIcon,
};
